import { Injectable } from '@angular/core';
import { Lov } from 'app/models/lov.model';
import { RegistrationTypeEnum } from 'app/models/lookup-enums.model';
import { Lookup, LinkLookup } from '@mrm/core';
import * as moment from 'moment';

@Injectable({
    providedIn: 'root'
})
export class GlobalsService {
    private _lov: Lov;
    private _regType: RegistrationTypeEnum;
    private _isRegistrationClosed: boolean;
    private _isPaymentClosed: boolean;
    private _pmtDeadline: string;
    private _utcOffset: number;
    private _requiresDeadlineValidation: boolean;
    private _fundingSource: string;

    constructor() { }

    // Public function to ensure the .value getter works.
    public assignValues(values: Lookup[]): Lookup[] {
        const lookups: Lookup[] = [];
        values.forEach(x => {
            const lookup = new Lookup();
            lookups.push(Object.assign(lookup, x));
        });

        return lookups;
    }

    // Public function to ensure the .value getter works.
    public assignLinkValues(values: LinkLookup[]): LinkLookup[] {
        if (values == null) { return []; }

        const lookups: LinkLookup[] = [];
        values.forEach(x => {
            const lookup = new LinkLookup();
            lookups.push(Object.assign(lookup, x));
        });

        return lookups;
    }

    get lov(): Lov {
        if (this._lov == undefined) {
            this._lov = JSON.parse(sessionStorage.getItem('lov'));
        }
        return this._lov;
    }
    set lov(value: Lov) {
        this._lov = value;
        sessionStorage.setItem('lov', JSON.stringify(value));
    }

    get provinces(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.province);
        }
        return [];
    }

    set provinces(value) {
        this._lov.province = this.assignValues(value);
    }

    get regions(): Lookup[] {
        if (this._lov != undefined && this._lov.region != null) {
            return this.assignValues(this._lov.region);
        }
        return [];
    }

    get salutations(): Lookup[] {
        if (this._lov != undefined) {
            const salutations = this.assignValues(this._lov.salutation);

            salutations.find(s => s.code.trim() === '').valueEnglish = '(Leave Blank)';
            salutations.find(s => s.code.trim() === '').valueFrench = '(Laisser vide)';

            return salutations;
        }

        return [];
    }

    get languages(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.language);
        }
        return [];
    }

    get genders(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.gender);
        }
        return [];
    }

    get executiveRoles(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.executiveRole);
        }
        return [];
    }

    get terms(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.term);
        }
        return [];
    }

    get attendeeTypes(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.attendeeType);
        }
        return [];
    }

    get mrmLanguages(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.mrmLanguage);
        }
        return [];
    }

    get conventionRegistrationStatuses(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.conventionRegistrationStatus);
        }
        return [];
    }

    get conferenceTypes(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.conferenceType);
        }
        return [];
    }

    get conferenceRegistrationStatuses(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.conferenceRegistrationStatus);
        }
        return [];
    }

    get conventionRegistrationTypes(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.conventionRegistrationType);
        }
        return [];
    }

    get addressTypes(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.addressType);
        }
        return [];
    }

    get phoneTypes(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.phoneType);
        }
        return [];
    }

    get electronicAddressTypes(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.electronicAddressType);
        }
        return [];
    }

    get minorityGroups(): LinkLookup[] {
        if (this._lov != undefined) {
            const minorityGroups = this.assignLinkValues(this._lov.minorityGroup);

            let group = minorityGroups.find(m => m.code == 'A');

            if (group != undefined) {
                group.valueEnglish = 'I am an Indigenous person (First Nations, Métis or Inuit)';
                group.valueFrench = 'Je suis une personne autochtone (Premières Nations, Métis ou Inuit)';
            }

            group = minorityGroups.find(m => m.code == 'K');

            if (group != undefined) {
                group.valueEnglish = 'I am a Black person';
                group.valueFrench = 'Je suis une personne noire';

            }

            group = minorityGroups.find(m => m.code == 'V');

            if (group != undefined) {
                group.valueEnglish = 'I am a racialized person';
                group.valueFrench = 'Je suis une personne racisée';

            }

            group = minorityGroups.find(m => m.code == 'P');

            if (group != undefined) {
                group.valueEnglish = 'I am a person with a disability';
                group.valueFrench = 'Je suis une personne en situation de handicap';

            }

            group = minorityGroups.find(m => m.code == 'Y');

            if (group != undefined) {
                group.valueEnglish = 'I am a young worker';
                group.valueFrench = 'Je suis une jeune travailleuse ou un jeune travailleur';

            }

            return minorityGroups;
        }
        return [];
    }

    set minorityGroups(value) {
        this._lov.minorityGroup = this.assignLinkValues(value);
    }

    get attendeeTypesConvention(): LinkLookup[] {
        if (this._lov != undefined && this._lov.attendeeTypeConvention != null) {
            return this.assignLinkValues(this._lov.attendeeTypeConvention);
        }
        return [];
    }

    set attendeeTypesConvention(value) {
        this._lov.attendeeTypeConvention = this.assignLinkValues(value);
    }

    get conventionGenders(): LinkLookup[] {
        if (this._lov != undefined && this._lov.conventionGender != null) {
            return this.assignLinkValues(this._lov.conventionGender);
        }
        return [];
    }

    set conventionGenders(value) {
        this._lov.conventionGender = this.assignLinkValues(value);
    }

    get conventionLanguages(): LinkLookup[] {
        if (this._lov != undefined && this._lov.conventionLanguage != null) {
            return this.assignLinkValues(this._lov.conventionLanguage);
        }
        return [];
    }

    set conventionLanguages(value) {
        this._lov.conventionLanguage = this.assignLinkValues(value);
    }

    get conferenceGenders(): LinkLookup[] {
        if (this._lov != undefined && this._lov.conferenceGender != null) {
            return this.assignLinkValues(this._lov.conferenceGender);
        }
        return [];
    }

    set conferenceGenders(value) {
        this._lov.conferenceGender = this.assignLinkValues(value);
    }

    get conferenceLanguages(): LinkLookup[] {
        if (this._lov != undefined && this._lov.conferenceLanguage != null) {
            return this.assignLinkValues(this._lov.conferenceLanguage);
        }
        return [];
    }

    set conferenceLanguages(value) {
        this._lov.conferenceLanguage = this.assignLinkValues(value);
    }

    get correspondenceLanguages(): LinkLookup[] {
        if (this._lov != undefined && this._lov.correspondenceLanguage != null) {
            return this.assignLinkValues(this._lov.correspondenceLanguage);
        }
        return [];
    }

    set correspondenceLanguages(value) {
        this._lov.correspondenceLanguage = this.assignLinkValues(value);
    }

    get conferenceExpertises(): LinkLookup[] {
        if (this._lov != undefined && this._lov.conferenceExpertise != null) {
            return this.assignLinkValues(this._lov.conferenceExpertise);
        }
        return [];
    }

    set conferenceExpertises(value) {
        this._lov.conferenceExpertise = this.assignLinkValues(value);
    }

    get conferencePreferences(): LinkLookup[] {
        if (this._lov != undefined && this._lov.conferencePreference != null) {
            return this.assignLinkValues(this._lov.conferencePreference);
        }
        return [];
    }

    set conferencePreferences(value) {
        this._lov.conferencePreference = this.assignLinkValues(value);
    }

    get conferenceDesignations(): LinkLookup[] {
        if (this._lov) {
            const conferenceDesignations = this.assignLinkValues(this._lov.conferenceDesignation);

            conferenceDesignations.find(m => m.code == 'A').valueEnglish = 'I am an Indigenous person (First Nations, Métis or Inuit)';
            conferenceDesignations.find(m => m.code == 'A').valueFrench = 'Je m’identifie comme une personne autochtone (Premières Nations, Métis ou Inuit)';

            conferenceDesignations.find(m => m.code == 'V').valueEnglish = 'I identify as Black and/or a racialized person or person of colour';
            conferenceDesignations.find(m => m.code == 'V').valueFrench = 'Je m’identifie comme une personne noire et/ou racisée ou une personne de couleur';

            conferenceDesignations.find(m => m.code == 'P').valueEnglish = 'I identify as a person with a disability';
            conferenceDesignations.find(m => m.code == 'P').valueFrench = 'Je m’identifie comme une personne ayant un handicap';

            conferenceDesignations.find(m => m.code == 'Y').valueEnglish = 'I consider myself a young worker';
            conferenceDesignations.find(m => m.code == 'Y').valueFrench = 'Je me considère comme étant une jeune travailleuse ou un jeune travailleur';

            return conferenceDesignations;
        }
        return [];
    }

    set conferenceDesignations(value) {
        this._lov.conferenceDesignation = this.assignLinkValues(value);
    }

    get conferenceSpecialNeeds(): LinkLookup[] {
        if (this._lov) {
            const conferenceSpecialNeeds = this.assignLinkValues(this._lov.conferenceSpecialNeed);

            // conferenceSpecialNeeds.find(m => m.code === 'W').valueEnglish = 'USB Flash Drive (Word)';
            // conferenceSpecialNeeds.find(m => m.code === 'W').valueFrench = '*F*USB Flash Drive (Word)';

            // conferenceSpecialNeeds.find(m => m.code === 'PD').valueEnglish = 'USB Flash Drive (PDF)';
            // conferenceSpecialNeeds.find(m => m.code === 'PD').valueFrench = '*F*USB Flash Drive (PDF)';

            // conferenceSpecialNeeds.find(m => m.code === 'OM').valueEnglish = 'Other (Specify)';
            // conferenceSpecialNeeds.find(m => m.code === 'OM').valueFrench = '*F*Other (Specify)';

            // conferenceSpecialNeeds.filter(x => ['OM', 'OS'].contains(x.code)).forEach(x => {
            //     x.valueEnglish = 'Other (Specify)';
            //     x.valueFrench = '*F*Other (Specify)';
            // });

            return conferenceSpecialNeeds;
        }
        return [];
    }

    set conferenceSpecialNeeds(value) {
        this._lov.conferenceSpecialNeed = this.assignLinkValues(value);
    }

    get conferenceNoteTypes(): LinkLookup[] {
        if (this._lov != undefined && this._lov.conferenceNoteType != null) {
            return this.assignLinkValues(this._lov.conferenceNoteType);
        }
        return [];
    }

    set conferenceNoteTypes(value) {
        this._lov.conferenceNoteType = this.assignLinkValues(value);
    }

    get conferenceContentTypes(): Lookup[] {
        if (this._lov != undefined && this._lov.conferenceContentType != null) {
            return this.assignValues(this._lov.conferenceContentType);
        }
        return [];
    }

    set conferenceContentTypes(value) {
        this._lov.conferenceContentType = this.assignValues(value);
    }

    get conferenceRegistrationTypes(): LinkLookup[] {
        if (this._lov != undefined && this._lov.conferenceRegistrationType != null) {
            return this.assignLinkValues(this._lov.conferenceRegistrationType);
        }
        return [];
    }

    set conferenceRegistrationTypes(value) {
        this._lov.conferenceRegistrationType = this.assignLinkValues(value);
    }

    get luiSectors(): LinkLookup[] {
        if (this._lov != undefined && this._lov.luiSector != null) {
            return this.assignLinkValues(this._lov.luiSector);
        }
        return [];
    }

    set luiSectors(value) {
        this._lov.luiSector = this.assignLinkValues(value);
    }

    get conferenceRegions(): LinkLookup[] {
        if (this._lov != undefined && this._lov.conferenceRegion != null) {
            return this.assignLinkValues(this._lov.conferenceRegion);
        }
        return [];
    }

    set conferenceRegions(value) {
        this._lov.conferenceRegion = this.assignLinkValues(value);
    }

    get occupationCodes(): LinkLookup[] {
        if (this._lov != undefined && this._lov.occupationCode != null) {
            return this.assignLinkValues(this._lov.occupationCode);
        }
        return [];
    }

    set occupationCodes(value) {
        this._lov.occupationCode = this.assignLinkValues(value);
    }

    get jurisdictions(): LinkLookup[] {
        if (this._lov != undefined && this._lov.jurisdiction != null) {
            return this.assignLinkValues(this._lov.jurisdiction);
        }
        return [];
    }

    set jurisdictions(value) {
        this._lov.jurisdiction = this.assignLinkValues(value);
    }

    get sectors(): LinkLookup[] {
        if (this._lov != undefined && this._lov.sector != null) {
            return this.assignLinkValues(this._lov.sector);
        }
        return [];
    }

    set sectors(value) {
        this._lov.sector = this.assignLinkValues(value);
    }

    get regType(): RegistrationTypeEnum {
        return this._regType;
    }

    set regType(regType: RegistrationTypeEnum) {
        this._regType = regType;
    }

    get fundingSource(): string {
        return this._fundingSource;
    }

    set fundingSource(fundingSource: string) {
        this._fundingSource = fundingSource;
    }

    get accessibilities(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.accessibilities);
        }
        return [];
    }

    get dietaryRequirements(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.dietaryRequirements);
        }
        return [];
    }

    // To handle virtual closing of registrations/payments.
    // Currently used only for Convention.
    public checkDeadlinesBasedOnCurrentTime() {
        // Set requiresDeadlineValidation if this is initially called by the app.
        // Since we call this method from registration-index.component.ts, we're ConventionDBID = 5.
        // This allows us to check this value from the save of each form (Delegate/Alternate/Observer)
        // since we don't need to check the ConventionDBID again.  This way we only have to have the ConventionDBID
        // logic in one place.
        this._requiresDeadlineValidation = true;

        // If date being set to UTC is Daylight Savings Time, then -0400, otherwise -0500
        const isDST = moment().isDST();
        const regDeadline = isDST ? '2021-11-17 21:00' : '2021-11-17 22:00'; // UTC time which is 5:00 PM our time
        this._pmtDeadline = isDST ? '2021-11-22 21:00' : '2021-11-22 22:00'; // UTC time which is 5:00 PM our time
        this._utcOffset = isDST ? -4 : -5; // Get the current offset based on DST for current date.
        const registrationDeadline = moment.utc(regDeadline).local();
        const paymentDeadline = moment.utc(this._pmtDeadline).local();
        this._isRegistrationClosed = registrationDeadline.isBefore(new Date());
        this._isPaymentClosed = paymentDeadline.isBefore(new Date());
    }

    get requiresDeadlineValidation(): boolean {
        // Set above when first calling checkDeadlinesBasedOnCurrentTime.
        return this._requiresDeadlineValidation;
    }

    get paymentDeadline(): string {
        return this._pmtDeadline;
    }

    get utcOffset(): number {
        return this._utcOffset;
    }

    get isRegistrationClosed(): boolean {
        return this._isRegistrationClosed;
    }

    get isPaymentClosed(): boolean {
        return this._isPaymentClosed;
    }



    get registrationSelfIdentificationTypes(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.registrationSelfIdentificationType);
        }
        return [];
    }

    get registrationGenderTypes(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.registrationGenderType);
        }
        return [];
    }

    get registrationSelfIdentificationSelectionTypes(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.registrationSelfIdentificationSelectionType);
        }
        return [];
    }

    get registrationSpecialNeedSelectionTypes(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.registrationSpecialNeedSelectionType);
        }
        return [];
    }

    get registrationSpecialNeedTypes(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.registrationSpecialNeedType);
        }
        return [];
    }

    get registrationSpecialNeedHearingAidTypes(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.registrationSpecialNeedHearingAidType);
        }
        return [];
    }

    get registrationSpecialNeedHotelRoomTypes(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.registrationSpecialNeedHotelRoomType);
        }
        return [];
    }

    get registrationSpecialNeedMobilityTypes(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.registrationSpecialNeedMobilityType);
        }
        return [];
    }

    get registrationSpecialNeedVisualAidTypes(): Lookup[] {
        if (this._lov != undefined) {
            return this.assignValues(this._lov.registrationSpecialNeedVisualAidType);
        }
        return [];
    }
}